import { useCallback, useLayoutEffect, useState, RefObject } from "react";

// Sub-pixel rounding can make scrollWidth exceed clientWidth on text that is not clipped.
const OVERFLOW_TOLERANCE_PX = 1;

/**
 * Reports whether the referenced element's content is visually clipped by `overflow: hidden`,
 * i.e. rendered with a CSS ellipsis.
 *
 * Measured after every render (content can change without the box resizing) and again whenever
 * the element itself is resized.
 */
export const useIsTruncated = <T extends HTMLElement>(ref: RefObject<T | null>): boolean => {
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const node = ref.current;

    if (!node) {
      return;
    }

    setIsTruncated(node.scrollWidth > node.clientWidth + OVERFLOW_TOLERANCE_PX);
  }, [ref]);

  useLayoutEffect(measure);

  useLayoutEffect(() => {
    const node = ref.current;

    // jsdom and other non-browser environments have no ResizeObserver;
    // the per-render measurement above still applies there.
    if (!node || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, [measure, ref]);

  return isTruncated;
};
